import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@reservamos/elements';
import PropTypes from 'prop-types';
import BillingContent from './BillingContent';

/**
 * BillingModal component.
 *
 * This component handles the billing/fiscal information update functionality.
 * It manages modal visibility, title, and responsive behavior.
 *
 * @param {object} props - showModal, onClose, purchaseToken, validateChangeDocument, makeChangeDocument, selectedTicket, modalType
 * @param {boolean} props.showModal - Controls visibility of the billing modal
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} props.purchaseToken - Token used for purchase operations
 * @param {Function} props.validateChangeDocument - Function to validate document changes
 * @param {Function} props.makeChangeDocument - Function to make document changes
 * @param {object} props.selectedTicket - The selected ticket with document information
 * @param {string} props.modalType - The type of billing modal ('invoice' or 'correction')
 *
 * @returns {JSX.Element} The rendered billing modal component.
 */
const BillingModal = ({
  showModal,
  onClose,
  purchaseToken,
  validateChangeDocument,
  makeChangeDocument,
  selectedTicket,
  modalType,
}) => {
  const { t } = useTranslation('exchange');

  /**
   * getModalTitle Function
   *
   * Returns the appropriate title based on the modal type.
   *
   * @returns {string} The modal title
   */
  const getModalTitle = () => {
    if (modalType === 'invoice') {
      return t('modal_confirm_billing.title_change_to_invoice');
    }
    if (modalType === 'correction') {
      return t('modal_confirm_billing.title_ruc_correction');
    }
    return t('modal_confirm_billing.title');
  };

  if (!showModal) {
    return null;
  }

  return (
    <Modal
      title={getModalTitle()}
      responsiveSize="XL"
      closeOnOverlayClick={false}
      closeOnEscape={false}
      onCloseModal={onClose}
    >
      <BillingContent
        purchaseToken={purchaseToken}
        validateChangeDocument={validateChangeDocument}
        makeChangeDocument={makeChangeDocument}
        selectedTicket={selectedTicket}
        modalType={modalType}
        onClose={onClose}
      />
    </Modal>
  );
};

BillingModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  purchaseToken: PropTypes.string.isRequired,
  validateChangeDocument: PropTypes.func.isRequired,
  makeChangeDocument: PropTypes.func.isRequired,
  selectedTicket: PropTypes.shape({
    companyCode: PropTypes.string.isRequired,
    documentType: PropTypes.string.isRequired,
    documentNumber: PropTypes.string.isRequired,
    documentPrefix: PropTypes.string,
  }).isRequired,
  modalType: PropTypes.oneOf(['invoice', 'correction']).isRequired,
};

export default BillingModal;
